/* here&found — YOWL use-illustration (assets/hf-mechanism-yowl.js).
   Verb: SHRIEKS. A soft blob with oversized eyes, uneven teeth, tongue and a
   crown of flexible hair waits on the desk; a hand pinches one strand, a short
   pull makes it yelp, and a longer pull produces a bigger reaction.

   The silhouette is taken from the product: squat scalloped rubber body,
   enormous offset eyes, open toothy mouth, tongue and long cord-like hair.
   Light stage, ink + cream only, with cobalt on one pulled strand and the
   sound marks. Pose cuts only; no audio is simulated or claimed. */

const K = window.HFIllo.kit;
const { q, boxFill, Sh, Hand, st, INK, CREAM } = K;
const COBALT = (getComputedStyle(document.documentElement).getPropertyValue('--accent-deep') || '#1B54D6').trim();

const FLOOR_Y = 272;

function room() {
  return `
    <path ${st(INK, 2.6)} d="${q(34, FLOOR_Y, 492, FLOOR_Y - 4, 2.4)}"/>
    <!-- one useful desk cue: a closed notebook -->
    ${Sh(418, FLOOR_Y - 4, 40, 5)}
    <path d="${boxFill(378, FLOOR_Y - 20, 82, 16, 2.2)}" fill="${CREAM}" stroke="${INK}" stroke-width="2.2"/>
    <path ${st(INK, 1.4)} d="${q(390, FLOOR_Y - 13, 448, FLOOR_Y - 14, 1.2)}"/>`;
}

function hair(pull = 0) {
  const strands = [
    [206, 126, 86, 112, -12], [214, 118, 103, 74, -16], [224, 112, 132, 48, -14],
    [238, 109, 174, 38, -8], [253, 110, 230, 39, 5], [268, 116, 292, 50, -5],
    [279, 123, 330, 74, -7], [286, 132, 348, 105, -5],
  ];
  const base = strands.map(([x1, y1, x2, y2, bow], i) =>
    `<path ${st(i === 2 ? COBALT : INK, i === 2 ? 3.2 : 2.6)} d="${q(x1, y1, x2 + (i === 2 ? pull : 0), y2 - (i === 2 ? pull * .55 : 0), bow)}"/>`).join('');
  return base;
}

function yowl({ squash = 0, mouth = 0, pull = 0, eyes = 0 } = {}) {
  const top = 116 + squash;
  const bottom = 250;
  const mouthRy = 25 + mouth;
  return `
    ${Sh(246, FLOOR_Y - 6, 76, 8)}
    ${hair(pull)}
    <!-- soft scalloped body -->
    <path d="M183 ${bottom} Q174 236 184 218 Q173 195 184 174 Q174 147 196 ${top + 12}
      Q216 ${top - 8} 242 ${top} Q270 ${top - 8} 294 ${top + 12}
      Q316 148 307 174 Q318 197 306 218 Q317 238 304 ${bottom}
      Q286 263 270 252 Q246 266 226 253 Q204 264 183 ${bottom} Z"
      fill="${CREAM}" stroke="${INK}" stroke-width="2.8" stroke-linejoin="round"/>
    <!-- eyes: deliberately oversized and slightly mismatched -->
    <ellipse cx="220" cy="160" rx="24" ry="27" fill="${CREAM}" stroke="${INK}" stroke-width="2.4"/>
    <ellipse cx="274" cy="158" rx="26" ry="29" fill="${CREAM}" stroke="${INK}" stroke-width="2.4"/>
    <circle cx="${224 + eyes}" cy="161" r="7" fill="${INK}"/>
    <circle cx="${278 + eyes}" cy="159" r="7" fill="${INK}"/>
    <!-- open mouth, uneven teeth and tongue -->
    <ellipse cx="249" cy="214" rx="45" ry="${mouthRy}" fill="none" stroke="${INK}" stroke-width="2.8"/>
    <path d="M218 198 Q224 192 231 199 L229 211 Q223 215 220 207 Z
             M240 194 Q248 190 254 198 L251 211 Q245 215 242 207 Z
             M268 197 Q276 193 281 201 L276 212 Q270 214 269 206 Z"
      fill="${CREAM}" stroke="${INK}" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M239 231 Q249 219 260 231 Q258 248 250 250 Q241 247 239 231 Z"
      fill="${CREAM}" stroke="${INK}" stroke-width="2"/>
    <!-- rubber side bumps -->
    <path ${st(INK, 1.5)} d="${q(190, 181, 181, 187, 1.5)} ${q(190, 197, 181, 203, 1.5)} ${q(306, 181, 315, 187, -1.5)} ${q(306, 198, 315, 204, -1.5)}"/>`;
}

const handAt = (x, y, rot) => `<g transform="translate(${2 * x} 0) scale(-1 1)">${Hand(x, y, rot)}</g>`;

const POSES = {
  wait: () => room() + yowl(),
  pinch: () => room() + yowl({ eyes: -2 }) + handAt(260, 51, -10),
  short: () => room() + yowl({ squash: 5, mouth: 6, pull: 16, eyes: -4 }) + handAt(276, 33, -4)
    + `<path ${st(COBALT, 2)} d="${q(332, 150, 349, 141, 2)} ${q(335, 162, 354, 162, -2)}"/>`,
  long: () => room() + yowl({ squash: 10, mouth: 12, pull: 34, eyes: -6 }) + handAt(294, 18, 2)
    + `<path ${st(COBALT, 2.2)} d="${q(334, 136, 358, 122, 3)} ${q(340, 151, 370, 149, -2)} ${q(333, 165, 360, 179, 2)}"/>`,
  reset: () => room() + yowl({ mouth: 2 }),
};

window.HFIllo.register('yowl', {
  label: 'A hand pulls one flexible strand on the soft desk creature; a short pull and a long pull trigger different yelps',
  night: false,
  settled: 'reset',
  phases: [['wait', 900], ['pinch', 650], ['short', 520], ['wait', 550], ['pinch', 550], ['long', 760], ['reset', 1800]],
  tags: {
    wait: 'The hair is the control',
    pinch: 'Pinch one strand',
    short: 'Short pull — short yelp',
    long: 'Long pull — bigger yelp',
    reset: 'Ready for another pull',
  },
  draw: (name) => (POSES[name] || POSES.reset)(),
});

window.HFIllo.define();
